import { t } from '@lingui/macro'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { css, keyframes, useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { breakpoints } from '@/config/layout'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { useInterceptBackHandler } from '@/modules/shared/hooks/useInterceptBackHandler'
import { useToast } from '@/modules/shared/hooks/useToast'
import { CloseBookingModal } from './components/CloseBookingModal'
import { StepRenderer } from './components/StepRenderer'
import { isPaginationRequest, useBooking } from './hooks/useBooking'

import type {
  ConfirmRequest,
  FlowState,
  FlowStep,
  FlowStepType,
  LoadMoreSlotsRequest,
  NavigationRequest,
  SelectAnswerRequest,
  SelectRecommendedCaregiverRequest,
  SelectSlotRequest,
  SelectSpecialisationRequest,
  UpdateFiltersRequest,
} from './types/booking.type'

export const BookingFlow = () => {
  const { showErrorToast } = useToast()
  const theme = useTheme()
  const navigate = useNavigate()
  const abortController = useRef<AbortController>(new AbortController())
  const isLoadingRef = useRef(false)
  const isLoadingMoreRef = useRef(false)
  const isInitialized = useRef<boolean>(false)
  const lastTrackedStep = useRef<FlowStepType>()

  const { trackingId, startBookingFlow, navigateBookingFlow } = useBooking()
  const [flowState, setFlowState] = useState<FlowState>()
  const [currentStep, setCurrentStep] = useState<FlowStep>()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showCloseModal, setShowCloseModal] = useState(false)
  const isClosingRef = useRef(false)

  useHideNativeNavigation()

  // Start the booking flow on component mount
  useEffect(() => {
    if (isInitialized.current) return
    isInitialized.current = true

    const initializeFlow = async () => {
      try {
        setIsLoading(true)
        mixpanel.track('booking_flow_initialize_started', {
          id: trackingId,
        })
        const response = await startBookingFlow()
        setFlowState(response.flowState)
        setCurrentStep(response.currentStep)

        mixpanel.track('booking_flow_initialize_succeeded', {
          id: trackingId,
          sessionId: response.flowState.sessionId,
          step: response.currentStep.type,
          withTriage: !!response.flowState.triagePath?.length,
          triagePathLength: response.flowState.triagePath?.length ?? 0,
        })
      } catch (error_) {
        showErrorToast({
          message: t({
            id: 'booking-flow.error_starting',
            message: 'Er is een fout opgetreden tijdens het starten van de boekingsmodule.',
          }),
        })

        console.error('Error starting booking flow:', {
          id: trackingId,
          error: error_,
        })

        mixpanel.track('booking_flow_initialize_failed', {
          id: trackingId,
          errorName: error_ instanceof Error ? error_.name : typeof error_,
        })
        mixpanel.track('booking_flow_error', {
          id: trackingId,
          step: 'start-session',
        })

        setFlowState({
          currentStep: 'error',
        })
        setCurrentStep({
          type: 'error',
          canGoBack: false,
          isFinal: true,
          title: t({
            id: 'booking_flow_error_title',
            comment: 'Booking flow error title',
            message: 'Fout bij het boeken',
          }),
        })
      } finally {
        setIsLoading(false)
      }
    }

    initializeFlow()
  }, [showErrorToast, startBookingFlow, trackingId])

  useEffect(() => {
    if (!currentStep?.type) return
    if (lastTrackedStep.current === currentStep.type) return

    lastTrackedStep.current = currentStep.type
    mixpanel.track('booking_flow_step_rendered', {
      id: trackingId,
      sessionId: flowState?.sessionId,
      step: currentStep.type,
      canGoBack: currentStep.canGoBack,
      isFinal: currentStep.isFinal,
      withTriage: !!flowState?.triagePath?.length,
      triagePathLength: flowState?.triagePath?.length ?? 0,
    })
  }, [
    currentStep?.canGoBack,
    currentStep?.isFinal,
    currentStep?.type,
    flowState?.sessionId,
    flowState?.triagePath?.length,
    trackingId,
  ])

  const handleNavigation = useCallback(async (request: NavigationRequest) => {
    const navigationStartedAt = typeof performance === 'undefined' ? Date.now() : performance.now()

    const isLoadMore = request.action === 'load-more-slots'
    const isFilterUpdate = request.action === 'update-filters'

    // Only the carousel's "next page" is deduplicated: selecting the last date fires
    // its click handler, its scroll listener and its refill effect, so one tap can
    // ask for the same page several times over. The other two requests that travel
    // as 'load-more-slots' — refreshing a single date, extending the search window —
    // are one per tap, and dropping them would discard work the patient asked for.
    const isPagination = isPaginationRequest(request)

    // Don't paginate while the base data is being replaced
    if (isLoadMore && isLoadingRef.current) return
    if (isPagination && isLoadingMoreRef.current) {
      return
    }

    // Capture the controller identity at dispatch time — if it changes before
    // the response arrives, this navigation was superseded.
    const myController = abortController.current

    try {
      if (isPagination) {
        isLoadingMoreRef.current = true
        setIsLoadingMore(true)
      } else if (!isLoadMore) {
        isLoadingRef.current = true
        // Filter updates keep the filter controls visible — the slot list
        // already shows a stale indicator via isStale from markFiltersChanged.
        if (!isFilterUpdate) {
          setIsLoading(true)
        }
      }

      mixpanel.track('booking_flow_navigation_started', {
        id: trackingId,
        action: request.action,
        fromStep: request.flowState.currentStep,
        sessionId: request.flowState.sessionId,
        withTriage: !!request.flowState.triagePath?.length,
        triagePathLength: request.flowState.triagePath?.length ?? 0,
      })

      const response = await navigateBookingFlow(request, myController)

      if (!response) {
        const durationMs =
          typeof performance === 'undefined'
            ? Math.round(Date.now() - navigationStartedAt)
            : Math.round(performance.now() - navigationStartedAt)

        mixpanel.track('booking_flow_navigation_aborted', {
          id: trackingId,
          action: request.action,
          fromStep: request.flowState.currentStep,
          sessionId: request.flowState.sessionId,
          durationMs,
          withTriage: !!request.flowState.triagePath?.length,
          triagePathLength: request.flowState.triagePath?.length ?? 0,
        })
        return
      }

      if (abortController.current !== myController) return

      setFlowState(response.flowState)
      setCurrentStep(response.currentStep)

      const durationMs =
        typeof performance === 'undefined'
          ? Math.round(Date.now() - navigationStartedAt)
          : Math.round(performance.now() - navigationStartedAt)

      mixpanel.track('booking_flow_navigation_succeeded', {
        id: trackingId,
        action: request.action,
        fromStep: request.flowState.currentStep,
        toStep: response.currentStep.type,
        sessionId: response.flowState.sessionId,
        durationMs,
        withTriage: !!response.flowState.triagePath?.length,
        triagePathLength: response.flowState.triagePath?.length ?? 0,
      })
    } catch (error_) {
      if (abortController.current !== myController) return

      if (error_ instanceof AxiosError && error_.response?.status === 409) {
        mixpanel.track('booking_flow_slot_lock_error_toast', {
          id: trackingId,
          action: request.action,
          step: request.flowState.currentStep,
          sessionId: request.flowState.sessionId,
          withTriage: !!request.flowState.triagePath?.length,
          triagePathLength: request.flowState.triagePath?.length ?? 0,
        })

        return showErrorToast({
          message: t({
            id: 'booking-flow.error-slot-locked',
            message: 'Deze afspraak is intussen al geboekt. Gelieve een nieuw moment te kiezen.',
          }),
        })
      }

      console.error('Error navigating booking flow:', {
        id: trackingId,
        action: request.action,
        step: request.flowState.currentStep,
        error: error_,
      })

      const durationMs =
        typeof performance === 'undefined'
          ? Math.round(Date.now() - navigationStartedAt)
          : Math.round(performance.now() - navigationStartedAt)

      mixpanel.track('booking_flow_navigation_failed', {
        id: trackingId,
        action: request.action,
        fromStep: request.flowState.currentStep,
        sessionId: request.flowState.sessionId,
        durationMs,
        withTriage: !!request.flowState.triagePath?.length,
        triagePathLength: request.flowState.triagePath?.length ?? 0,
        errorName: error_ instanceof Error ? error_.name : typeof error_,
      })
      mixpanel.track('booking_flow_error', {
        id: trackingId,
        step: request.action,
        withTriage: !!request.flowState.triagePath?.length,
      })

      // Loading another page of dates is a background request: the patient is
      // already looking at a usable slot list, so a failure there must not
      // replace it with the error screen.
      if (isLoadMore) {
        return showErrorToast({
          message: t({
            id: 'booking-flow.error-loading-more-slots',
            message: 'We konden geen extra momenten laden. Probeer het later opnieuw.',
          }),
        })
      }

      setCurrentStep({
        type: 'error',
        canGoBack: true,
        isFinal: true,
        title: t({
          id: 'booking_flow_error_title',
          comment: 'Booking flow error title',
          message: 'Fout bij het boeken',
        }),
      })
    } finally {
      if (isPagination) {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
      if (abortController.current === myController) {
        setIsLoading(false)
        isLoadingRef.current = false
      }
    }
  }, [])

  const handleAction = useCallback(
    (action: NavigationRequest['action'], additionalData?: Record<string, unknown>) => {
      if (!flowState) return

      // Create properly typed navigation request based on action
      let request: NavigationRequest

      switch (action) {
        case 'select-recommended-caregiver': {
          const { recommendedCaregiverId } = additionalData as Omit<SelectRecommendedCaregiverRequest, 'action'>
          request = {
            action,
            flowState,
            recommendedCaregiverId,
          }
          break
        }
        case 'select-specialisation': {
          const { specialisationId } = additionalData as Omit<SelectSpecialisationRequest, 'action'>
          request = {
            action,
            flowState,
            specialisationId,
          }
          break
        }
        case 'select-answer': {
          const { answer } = additionalData as Omit<SelectAnswerRequest, 'action'>
          request = {
            action,
            flowState,
            answer,
          }
          break
        }
        case 'select-slot': {
          abortController.current.abort('Slot selected')
          abortController.current = new AbortController()

          const { slot } = additionalData as Omit<SelectSlotRequest, 'action'>
          request = {
            action,
            flowState,
            slot,
          }
          break
        }
        case 'update-filters': {
          abortController.current.abort('Superseded by newer filter update')
          abortController.current = new AbortController()

          const { filters } = additionalData as Omit<UpdateFiltersRequest, 'action'>
          request = {
            action,
            flowState,
            filters,
          }
          break
        }
        case 'load-more-slots': {
          const { fromDate, tillDate, numberOfDays } = additionalData as Omit<LoadMoreSlotsRequest, 'action'>
          request = {
            action,
            flowState,
            fromDate,
            tillDate,
            numberOfDays,
          }

          break
        }
        case 'confirm': {
          const { remark } = additionalData as Omit<ConfirmRequest, 'action'>
          request = {
            action,
            flowState,
            remark,
          }
          break
        }
        case 'disclaimer-submit':
        case 'continue-from-info': {
          request = {
            action,
            flowState,
          }
          break
        }
        case 'previous': {
          abortController.current.abort('Previous navigation requested')
          abortController.current = new AbortController()

          request = {
            action,
            flowState,
          }
          break
        }
        default: {
          console.error('Unknown action:', action)
          return
        }
      }

      return handleNavigation(request)
    },
    [flowState, handleNavigation],
  )

  const handleCloseBooking = useCallback(() => {
    isClosingRef.current = true
    mixpanel.track('booking_flow_abandoned', {
      step: currentStep?.type,
      withTriage: !!flowState?.triagePath?.length,
    })

    navigate('/home', { replace: true })
  }, [currentStep?.type, flowState?.triagePath?.length, navigate])

  const handleClose = useCallback(() => {
    mixpanel.track('booking_flow_show_close_warning', {
      step: currentStep?.type,
      withTriage: !!flowState?.triagePath?.length,
    })

    if (currentStep?.type === 'recommended-caregivers' || currentStep?.type === 'success') {
      handleCloseBooking()
      return
    }

    setShowCloseModal(true)
  }, [currentStep?.type, flowState?.triagePath?.length, handleCloseBooking])

  const handleHideCloseModal = useCallback(() => {
    mixpanel.track('booking_flow_resume', {
      step: currentStep?.type,
      withTriage: !!flowState?.triagePath?.length,
    })
    setShowCloseModal(false)
  }, [currentStep?.type, flowState?.triagePath?.length])

  const handleGoBack = useCallback(() => {
    if (!flowState || !currentStep?.canGoBack) return
    handleAction('previous')
  }, [flowState, currentStep, handleAction])

  const onGoBackHandler = useCallback(() => {
    if (isClosingRef.current || showCloseModal) {
      return false
    }

    if (!flowState || !currentStep?.canGoBack) {
      handleClose()
      return true
    }

    handleAction('previous')
    return true
  }, [flowState, currentStep, handleAction, showCloseModal, handleClose])

  useInterceptBackHandler(onGoBackHandler)

  if (isClosingRef.current) {
    return null
  }

  if (isLoading && !currentStep) {
    return <AnimatedLoader />
  }
  if (!currentStep) {
    return null
  }

  const pageBackgroundColor = ['emergency', 'contact', 'error'].includes(currentStep.type)
    ? theme.Colors.Background.Light
    : undefined

  return (
    <>
      <ContentPageLayout
        backgroundColor={pageBackgroundColor}
        noPadding
        noScroll
        header={
          <StContentContainer $backgroundColor={pageBackgroundColor}>
            <AppHeader
              title={{
                title: t({
                  id: 'booking-flow.title',
                  message: 'Nieuwe afspraak maken',
                }),
                as: 'h2',
              }}
              primaryAction={
                currentStep.canGoBack
                  ? {
                      label: t({
                        id: 'globals.previous-step',
                        comment: 'Global for previous step',
                        message: 'Vorige stap',
                      }),
                      icon: ['far', 'chevron-left'],
                      onClick: handleGoBack,
                    }
                  : undefined
              }
              secondaryAction={{
                label: t({
                  id: 'globals.close',
                  comment: 'Global for close',
                  message: 'Sluiten',
                }),
                icon: ['far', 'close'],
                onClick: handleClose,
              }}
            />
          </StContentContainer>
        }
        showHeaderAlways
      >
        <StBodyContainer $backgroundColor={pageBackgroundColor}>
          <StNavigationLoadingBar $visible={isLoading} />
          <StepRenderer
            step={currentStep}
            flowState={flowState!}
            onAction={handleAction}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
          />
        </StBodyContainer>
      </ContentPageLayout>

      <CloseBookingModal isVisible={showCloseModal} onAction={handleCloseBooking} onClose={handleHideCloseModal} />
    </>
  )
}

// Styled components

const StContentContainer = styled(FlexColumn)<{
  $backgroundColor?: string
}>`
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  background-color: ${({ $backgroundColor, theme }) => $backgroundColor || theme.Colors.Background.White};
  border-bottom: 1px solid
    ${({ theme }) => theme.Components.Card.DisabledBorder};

  @media ${breakpoints.fromS} {
    padding-bottom: 0;
  }
`

const StBodyContainer = styled(FlexColumn)<{
  $backgroundColor?: string
}>`
  height: 100%;
  width: 100%;
  overflow: hidden;
  flex: 1;
  background-color: ${({ $backgroundColor, theme }) => $backgroundColor || theme.Colors.Background.White};
`

const sweep = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`

const StNavigationLoadingBar = styled.div<{ $visible: boolean }>`
  position: relative;
  height: 3px;
  width: 100%;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.Colors.Neutrals.Lighter};
  overflow: hidden;
  transition: opacity 0.2s ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};

  ${({ $visible }) =>
    $visible &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset-block: 0;
        width: 40%;
        background-color: ${({ theme }) => theme.Colors.Theme.Primary};
        animation: ${sweep} 1.2s ease-in-out infinite;
      }
    `}
`
